import ProgressBar from '@ramonak/react-progress-bar'

import { type FunctionComponent } from 'react'

import useSecureScoresQuery from '~/hooks/services/soteria/queries/useSecureScoresQuery'

import TenantSelector from '~/components/Common/TenantSelector'

import { classNames } from '~/utils/classNames'

import { Iconly } from 'react-iconly'
import { useSelector } from 'react-redux'
import { type RootStateOrAny } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Tile, TileHeader, TileListContent, TileListItem } from '../Tile'

const ICON_BUTTON_CLASSES = 'flex h-8 w-8 flex-none items-center justify-center rounded-full bg-th-content-secondary'

const SecureScoresTile: FunctionComponent = () => {
  const navigate = useNavigate()
  const tenant = useSelector((state: RootStateOrAny) => state.tenant)

  const { data: secureScores, isLoading: isSecureScoresLoading } = useSecureScoresQuery(tenant.id)

  const m365Percentage = secureScores?.currentSecureScore?.scorePercent ?? 0
  const azurePercentage = (secureScores?.azureSecureScore?.score?.percentage ?? 0) * 100

  const getColorForScore = (score: number) => {
    return score <= 33 ? 'var(--error)' : score <= 66 ? 'var(--warning)' : 'var(--success)'
  }

  return (
    <Tile>
      <TileHeader loading={isSecureScoresLoading}>
        <TileHeader.Title>Secure Scores</TileHeader.Title>
        <TileHeader.Subtitle>Microsoft 365 and Azure Security Status</TileHeader.Subtitle>
        <TileHeader.Item>
          <TenantSelector />
        </TileHeader.Item>
      </TileHeader>

      <TileListContent>
        <TileListItem key="m365">
          <TileListItem.Title>Microsoft 365</TileListItem.Title>
          <div className="flex items-center">
            <ProgressBar
              completed={m365Percentage}
              maxCompleted={100}
              isLabelVisible={false}
              bgColor={getColorForScore(m365Percentage)}
              baseBgColor="var(--border)"
              className="flex-1"
            />
            <span className="w-24 text-center">{m365Percentage.toFixed(0)}%</span>
            <button
              className={classNames(ICON_BUTTON_CLASSES, 'cursor-pointer duration-100 hover:brightness-125')}
              onMouseDown={() => navigate('/m365_secure_score')}
            >
              <Iconly name="ChevronRight" set="light" className="h-5 w-5" />
            </button>
          </div>
        </TileListItem>

        <TileListItem key="azure">
          <TileListItem.Title>Azure</TileListItem.Title>
          <div className="flex items-center">
            <ProgressBar
              completed={azurePercentage}
              maxCompleted={100}
              isLabelVisible={false}
              bgColor={getColorForScore(azurePercentage)}
              baseBgColor="var(--border)"
              className="flex-1"
            />
            <span className="w-24 text-center">{azurePercentage.toFixed(0)}%</span>
            <button
              className={classNames(ICON_BUTTON_CLASSES, 'cursor-pointer duration-100 hover:brightness-125')}
              onMouseDown={() => navigate('/azure_secure_score')}
            >
              <Iconly name="ChevronRight" set="light" className="h-5 w-5" />
            </button>
          </div>
        </TileListItem>
      </TileListContent>
    </Tile>
  )
}

export default SecureScoresTile
